import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

const SESSION_KEY_NAVIGATED_IN_APP = 'appState.hasNavigatedInApp';

/**
 * App-wide UI and auth state: token, login modal request, language changes,
 * and navigation context (e.g. in-app click vs address bar / back) for guards.
 */
@Injectable({
    providedIn: 'root'
})
export class AppStateService {

    /** Last navigation trigger: 'imperative' (link/ navigate), 'popstate' (back/forward), 'hashchange'. */
    lastNavigationTrigger: 'imperative' | 'popstate' | 'hashchange' | null = null;

    constructor(private router: Router) {
        this.router.events
            .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
            .subscribe((e) => {
                this.lastNavigationTrigger = e.navigationTrigger;
            });
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe(() => {
                sessionStorage.setItem(SESSION_KEY_NAVIGATED_IN_APP, 'true');
            });
    }

    /** True if this tab has had at least one in-app navigation (so current nav is likely from a click). */
    hasNavigatedInApp(): boolean {
        return sessionStorage.getItem(SESSION_KEY_NAVIGATED_IN_APP) === 'true';
    }

    tokenSubject$ = new BehaviorSubject<any>(null);
    tokenObservable = () => this.tokenSubject$.asObservable();
    nextTokenValue = (val: any) => this.tokenSubject$.next(val);

    loginSubject$ = new BehaviorSubject<any>(null);
    loginObservable = () => this.loginSubject$.asObservable();
    nextLoginValue = (val: any) => this.loginSubject$.next(val);

    /** True while the Keycloak callback is exchanging the code for a token (header disables login). */
    loginLoadingSubject$ = new BehaviorSubject<boolean>(false);
    loginLoadingObservable = () => this.loginLoadingSubject$.asObservable();
    nextLoginLoadingValue = (val: boolean) => this.loginLoadingSubject$.next(val);

    localeSubject$ = new BehaviorSubject<any>(null);
    localeObservable = () => this.localeSubject$.asObservable();
    nextLocaleValue = (val: any) => this.localeSubject$.next(val);
}
